import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface DefectRow {
  readonly defectId: string;
  readonly defectCode: string;
  readonly summary: string;
  readonly severity: string;
  readonly priority: string;
  readonly status: string;
}

const STATUSES = ['', 'NEW', 'ASSIGNED', 'IN_PROGRESS', 'FIXED', 'RETEST', 'VERIFIED', 'CLOSED', 'REJECTED', 'DEFERRED', 'DUPLICATE'];
const SEVERITIES = ['', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'COSMETIC'];
const CREATE_SEVERITIES = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'COSMETIC'];

/**
 * M34 — Defect Management Dashboard (§31 / §13 / §14 / §15). Register of defects
 * (T453) across the ten-state lifecycle with severity and priority. BR-02:
 * critical defects must be closed before production. Supports raising a defect
 * and driving it — assign, start, fix, retest, verify, close, reject and delete —
 * each wired to the guarded /v1/qms/defects write API. Filterable by status and
 * severity.
 */
export function DefectManagementDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<DefectRow[]>([]);
  const [status, setStatus] = useState('');
  const [severity, setSeverity] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newSummary, setNewSummary] = useState('');
  const [newSeverity, setNewSeverity] = useState('MEDIUM');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (severity) params.set('severity', severity);
    const qs = params.toString();
    apiRequest<{ data: DefectRow[] }>(`/v1/qms/defects${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, status, severity, reload]);

  const failMessage = (err: unknown, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m34.common.notAuthorised', { perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newSummary.trim()) { setActionError(t('m34.defect.validation')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/qms/defects`, {
        method: 'POST',
        body: { summary: newSummary.trim(), severity: newSeverity },
        token,
      });
      setNotice(t('m34.defect.raised', { summary: newSummary.trim() }));
      setNewSummary('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, 'M34_DEFECT_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: DefectRow, path: string, label: string, perm: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/qms/defects/${r.defectId}/${path}`, { method: 'POST', body: {}, token });
      setNotice(t('m34.defect.notice', { code: r.defectCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, perm));
    }
  };

  const remove = async (r: DefectRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/qms/defects/${r.defectId}`, { method: 'DELETE', token });
      setNotice(t('m34.defect.notice', { code: r.defectCode, label: t('m34.defect.act.deleted') }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, 'M34_DEFECT_MANAGE'));
    }
  };

  return (
    <main aria-label="defect-dashboard">
      <h1>{t('m34.defect.title')}</h1>

      <section aria-label="create-defect">
        <h2>{t('m34.defect.raiseHeading')}</h2>
        <form aria-label="defect-form" onSubmit={(e) => void create(e)}>
          <label>{t('m34.defect.summary')}<input aria-label="field-defect-summary" value={newSummary} onChange={(e) => setNewSummary(e.target.value)} /></label>
          <label>{t('m34.defect.severity')}
            <select aria-label="field-defect-severity" value={newSeverity} onChange={(e) => setNewSeverity(e.target.value)}>
              {CREATE_SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-defect" disabled={busy}>{busy ? t('m34.defect.raising') : t('m34.defect.raiseBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="defect-action-error">{actionError}</p>}
        {notice && <p aria-label="defect-action-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m34.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m34.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m34.defect.severity')}
          <select aria-label="filter-severity" value={severity} onChange={(e) => setSeverity(e.target.value)}>
            {SEVERITIES.map((s) => <option key={s || 'all'} value={s}>{s || t('m34.common.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m34.common.loading')}</p> : (
        <>
          <p aria-label="defect-count">{t('m34.defect.count', { n: rows.length })}</p>
          <DataTable
            ariaLabel="defect-table"
            rows={rows}
            rowKey={(r) => r.defectId}
            columns={[
              { key: 'defectCode', header: t('m34.common.code'), render: (r) => r.defectCode },
              { key: 'summary', header: t('m34.defect.summary'), render: (r) => r.summary },
              { key: 'severity', header: t('m34.defect.severity'), render: (r) => r.severity },
              { key: 'priority', header: t('m34.common.priority'), render: (r) => r.priority },
              { key: 'status', header: t('m34.common.status'), render: (r) => r.status },
              { key: 'actions', header: t('m34.common.actions'), render: (r) => (
                <>
                  {r.status === 'NEW' && (
                    <>
                      <Button variant="secondary" type="button" aria-label={`assign-${r.defectCode}`} onClick={() => void act(r, 'assign', t('m34.defect.act.assigned'), 'M34_DEFECT_MANAGE')}>{t('m34.defect.btnAssign')}</Button>
                      <Button variant="danger" type="button" aria-label={`reject-${r.defectCode}`} onClick={() => void act(r, 'reject', t('m34.defect.act.rejected'), 'M34_DEFECT_MANAGE')}>{t('m34.defect.btnReject')}</Button>
                    </>
                  )}
                  {r.status === 'ASSIGNED' && (
                    <Button variant="secondary" type="button" aria-label={`start-${r.defectCode}`} onClick={() => void act(r, 'start', t('m34.defect.act.inProgress'), 'M34_DEFECT_MANAGE')}>{t('m34.defect.btnStart')}</Button>
                  )}
                  {r.status === 'IN_PROGRESS' && (
                    <Button variant="secondary" type="button" aria-label={`fix-${r.defectCode}`} onClick={() => void act(r, 'fix', t('m34.defect.act.fixed'), 'M34_DEFECT_MANAGE')}>{t('m34.defect.btnFix')}</Button>
                  )}
                  {r.status === 'FIXED' && (
                    <Button variant="secondary" type="button" aria-label={`retest-${r.defectCode}`} onClick={() => void act(r, 'retest', t('m34.defect.act.inRetest'), 'M34_DEFECT_MANAGE')}>{t('m34.defect.btnRetest')}</Button>
                  )}
                  {r.status === 'RETEST' && (
                    <Button variant="secondary" type="button" aria-label={`verify-${r.defectCode}`} onClick={() => void act(r, 'verify', t('m34.defect.act.verified'), 'M34_DEFECT_VERIFY')}>{t('m34.defect.btnVerify')}</Button>
                  )}
                  {r.status === 'VERIFIED' && (
                    <Button variant="secondary" type="button" aria-label={`close-${r.defectCode}`} onClick={() => void act(r, 'close', t('m34.defect.act.closed'), 'M34_DEFECT_VERIFY')}>{t('m34.defect.btnClose')}</Button>
                  )}
                  {!['CLOSED', 'REJECTED', 'DUPLICATE'].includes(r.status) && (
                    <Button variant="danger" type="button" aria-label={`delete-${r.defectCode}`} onClick={() => void remove(r)}>{t('m34.common.delete')}</Button>
                  )}
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
